import { inject, Injectable, InjectionToken } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FeatureFlagService, IFeatureFlagKeys } from '@lassi/config';
import { RouteUtilsService, ToastService } from '@lassi/webapp-shared';
import { IStepOption, TourService } from 'ngx-ui-tour-md-menu';
import { debounceTime, map, merge, switchMap, take, tap } from 'rxjs';

export const ONBOARDING_STORAGE = new InjectionToken<Storage>(
  'ONBOARDING_STORAGE',
  {
    factory: () => window.localStorage,
  },
);

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private readonly tour = inject(TourService);
  private readonly routeUtils = inject(RouteUtilsService);
  private readonly router = inject(Router);
  private readonly storage = inject(ONBOARDING_STORAGE);
  private readonly toast = inject(ToastService);
  private readonly featureFlagService = inject(FeatureFlagService);

  private readonly ONBOARDING_KEY = 'lassi.onboarding-completed';

  readonly end$ = this.tour.end$;

  isActive = toSignal(
    merge(
      this.tour.start$.pipe(map(() => true)),
      this.tour.end$.pipe(map(() => false)),
    ),
    {
      initialValue: false,
    },
  );

  shouldRunOnboarding() {
    if (
      !this.featureFlagService.isFeatureEnabled(
        IFeatureFlagKeys.enableOnboarding,
      )
    ) {
      return false;
    }

    const url = new URL(this.router.url, window.location.origin);
    const param = url.searchParams.get(
      RouteUtilsService.FORCE_ONBOARDING_QUERY_KEY,
    );
    return !!param || !this.storage.getItem(this.ONBOARDING_KEY);
  }

  start(steps: IStepOption[]) {
    this.tour.initialize(steps, {
      endBtnTitle: 'Finish Tour',
      allowUserInitiatedNavigation: true,
    });

    return this.routeUtils.routeEvents$.pipe(
      debounceTime(500),
      take(1),
      map(() => this.router.url), // save the current route before tour starts
      tap({
        next: () => {
          this.tour.startAt(0);
        },
      }),
      switchMap((initRoute) =>
        this.tour.end$.pipe(
          take(1),
          switchMap(() => {
            const url = new URL(initRoute, window.location.origin);
            url.searchParams.delete(
              RouteUtilsService.FORCE_ONBOARDING_QUERY_KEY,
            );
            return this.router.navigateByUrl(url.pathname + url.search);
          }),
        ),
      ),
      tap({
        next: () => {
          this.storage.setItem(this.ONBOARDING_KEY, 'true');
        },
      }),
    );
  }

  end() {
    this.tour.end();
  }

  skipTour() {
    this.tour.end();
    this.toast.showInfoMessage(
      'You can always view the tour again in the help section',
    );
  }
}
